import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import type { Appointment } from '@/api/appointments/appointmentsApi.types';
import { Icon } from '@/components/common/Icon';
import { Button } from '@/components/ui/button';
import { useDismissRescheduleRequestMutation } from '@/hooks/useDismissRescheduleRequestMutation';
import { formatIsoDateTimeToTimestamp } from '@/utils/dateFormatting';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';

const CLIENT_CANCELLATION_SOURCE = 'client';

interface ClientRequestNoticeProps {
  appointment: Appointment;
  onDismissed: () => void;
}

export function ClientRequestNotice({ appointment, onDismissed }: ClientRequestNoticeProps) {
  const { t } = useTranslation('appointments');
  const dismissMutation = useDismissRescheduleRequestMutation();

  function dismiss() {
    dismissMutation.mutate(appointment.id, {
      onSuccess: function done() {
        showSuccessNotification(t('clientRequest.dismissed'));
        onDismissed();
      },
      onError: function fail(error) {
        const message = resolveApiErrorMessage(error);
        showErrorNotification(message.length > 0 ? message : t('clientRequest.dismissError'));
      },
    });
  }

  if (appointment.cancelledBy === CLIENT_CANCELLATION_SOURCE) {
    return (
      <div className="flex items-start gap-2 p-3 rounded-xl bg-zooerrorbg text-zooerrortext text-sm">
        <Icon name="X" className="mt-0.5 shrink-0" />
        <span>{t('clientRequest.cancelledByClient')}</span>
      </div>
    );
  }

  if (appointment.rescheduleRequestedAt === null) {
    return null;
  }

  return (
    <div className="p-3 rounded-xl bg-zoowarningbg space-y-2">
      <div className="flex items-start gap-2 text-sm text-zoowarningtext font-semibold">
        <Icon name="RefreshCw" className="mt-0.5 shrink-0" />
        <span>
          {t('clientRequest.rescheduleRequested', {
            date: formatIsoDateTimeToTimestamp(appointment.rescheduleRequestedAt),
          })}
        </span>
      </div>
      {appointment.rescheduleRequestMessage === null ? null : (
        <p className="text-sm text-zooindigo whitespace-pre-wrap">
          {appointment.rescheduleRequestMessage}
        </p>
      )}
      <p className="text-xs text-zoomuted">{t('clientRequest.resolveHint')}</p>
      <Button
        variant="outline"
        size="chip"
        isLoading={dismissMutation.isPending}
        onClick={dismiss}
      >
        {t('clientRequest.dismiss')}
      </Button>
    </div>
  );
}
